/**
 * Pure helpers for Current Reimbursements CAM / Mgmt / Insurance / Taxes
 * checkbox + PSF persistence.
 */

/**
 * Normalize HAS_* fields from the API/DB into a boolean or null (unset).
 * Accepts "1"/"0", 1/0, and booleans — older loaders only accepted strings.
 */
function getCheckboxValue(tab, typesDataRow, key) {
    const raw = tab === 'proforma-reimbursement-tab'
        ? typesDataRow[`PRO_FORMA_${key}`]
        : tab === 'current-reimbursement-tab'
            ? typesDataRow[key]
            : undefined;

    if (raw === true || raw === 1 || raw === '1') return true;
    if (raw === false || raw === 0 || raw === '0') return false;
    return null;
}

/**
 * PSF columns are decimal(15,8). Keep expense-recomputed floats at that
 * precision so Pricing totals (e.g. $87,414 / $18.07 after CAM/Mgmt toggle)
 * survive Submit — 2-decimal storage rounded ~8.1267→8.13 and snapped back
 * to $87,453 / $18.08 on reload.
 */
const REIMB_PSF_DECIMALS = 8;
const REIMB_PSF_FACTOR = Math.pow(10, REIMB_PSF_DECIMALS);

function roundReimbPsf(value) {
    const num = parseFloat(value);
    if (!num || !isFinite(num)) {
        return 0;
    }
    return Math.round(num * REIMB_PSF_FACTOR) / REIMB_PSF_FACTOR;
}

/**
 * Serialize a reimbursement-type number cell for save.
 * Must preserve 0 (unchecked CAM/mgmt PSF); `value && parseFloat(value)`
 * previously stored null for 0 and caused totals to revert after reload.
 */
function serializeTenantTypeNumber(value) {
    if (value === null || value === '' || value === undefined) {
        return null;
    }
    const parsed = parseFloat(value);
    if (Number.isNaN(parsed)) {
        return null;
    }
    if (parsed === 0) {
        return 0;
    }
    return roundReimbPsf(parsed);
}

function serializeTenantTypeCheckbox(value) {
    return value ? 1 : 0;
}

/**
 * Treat checkbox-ish values as checked/unchecked for dirty detection.
 * null/undefined/'' count as unchecked so defaults vs explicit false still save.
 */
function isCheckboxChecked(value) {
    return value === true || value === 1 || value === '1';
}

function tenantTypeFieldChanged(type, value, originalValue) {
    if (type === 'checkbox') {
        const explicitlyOff = (v) => v === false || v === 0 || v === '0';
        // Explicit uncheck must save even when the DB value was unset (null).
        // Otherwise Submit reloads defaults and Total Exp. Reimb. snaps back.
        if (explicitlyOff(value)) {
            return !explicitlyOff(originalValue);
        }
        if (explicitlyOff(originalValue)) {
            return isCheckboxChecked(value);
        }
        return isCheckboxChecked(value) !== isCheckboxChecked(originalValue);
    }
    if (type === 'number') {
        const a = serializeTenantTypeNumber(value);
        const b = serializeTenantTypeNumber(originalValue);
        if (a === null && b === null) return false;
        if (a === null || b === null) return true;
        return Number(a) !== Number(b);
    }
    return value !== originalValue;
}

/**
 * Map UNDERWRITING_TENANT_TYPES rows by REIMB_TYPES_INDEX.
 * Load/save previously used array position, which breaks when indices are
 * sparse, 1-based, or out of order — updates then miss the NNN row and
 * Total Exp. Reimb. reloads the old values after Submit.
 */
function indexTenantTypesByReimbIndex(rows) {
    const byIndex = {};
    if (!Array.isArray(rows)) {
        return byIndex;
    }
    rows.forEach((row) => {
        if (!row || row.REIMB_TYPES_INDEX === null || row.REIMB_TYPES_INDEX === undefined || row.REIMB_TYPES_INDEX === '') {
            return;
        }
        const idx = parseInt(row.REIMB_TYPES_INDEX, 10);
        if (Number.isNaN(idx)) {
            return;
        }
        byIndex[idx] = row;
    });
    return byIndex;
}

function resolveTenantTypeRow(byIndex, uiIndex, fallbackArray) {
    if (byIndex[uiIndex]) {
        return byIndex[uiIndex];
    }
    // Some legacy Arc rows used 1-based indexes for codes 1..N
    if (byIndex[uiIndex + 1]) {
        return byIndex[uiIndex + 1];
    }
    if (Array.isArray(fallbackArray) && fallbackArray[uiIndex]) {
        return fallbackArray[uiIndex];
    }
    return {};
}

/**
 * Whether computeReimbTypes should overwrite PSF cells from Expenses.
 * Flags default to false; only an explicit true means expenses/sqft changed.
 * (Checking !== null incorrectly treated false as dirty on every mount/submit.)
 */
function shouldRecomputeReimbTypes({
    newCam = null,
    newSqft = null,
    newMgmt = null,
    newInsurance = null,
    newTaxes = null,
} = {}) {
    return {
        cam: newCam === true || newSqft === true,
        mgmt: newMgmt === true || newSqft === true,
        insurance: newInsurance === true || newSqft === true,
        taxes: newTaxes === true || newSqft === true,
    };
}

function anyReimbTypeRecomputeNeeded(flags) {
    return !!(flags.cam || flags.mgmt || flags.insurance || flags.taxes);
}

/**
 * PSF contributed by a reimbursement category given its checkbox.
 * Unchecked → 0 so Total Exp. Reimb. excludes that line.
 */
function computeReimbCategoryPsf(checkbox, expensePsf) {
    if (!checkbox) {
        return 0;
    }
    return roundReimbPsf(expensePsf);
}

/**
 * Checkbox fields that must always be saved together with their PSF value.
 * Unchecking sets PSF to 0; omitting the value on save left the old PSF in DB
 * and Total Exp. Reimb. snapped back after reload.
 */
const TENANT_TYPE_CHECKBOX_VALUE_PAIRS = [
    {hasKey: 'REIMB_TYPES_HAS_CAM', valueKey: 'REIMB_TYPES_CAM_VALUE', hasIndex: 3, valueIndex: 4},
    {hasKey: 'REIMB_TYPES_HAS_MANAGEMENT_FEE', valueKey: 'REIMB_TYPES_MGMT_FEE_VALUE', hasIndex: 5, valueIndex: 6},
    {hasKey: 'REIMB_TYPES_HAS_INSURANCE', valueKey: 'REIMB_TYPES_INSURANCE_VALUE', hasIndex: 7, valueIndex: 8},
    {hasKey: 'REIMB_TYPES_HAS_TAXES', valueKey: 'REIMB_TYPES_TAXES_VALUE', hasIndex: 9, valueIndex: 10},
    {hasKey: 'PRO_FORMA_REIMB_TYPES_HAS_CAM', valueKey: 'PRO_FORMA_REIMB_TYPES_CAM_VALUE', hasIndex: 3, valueIndex: 4},
    {hasKey: 'PRO_FORMA_REIMB_TYPES_HAS_MANAGEMENT_FEE', valueKey: 'PRO_FORMA_REIMB_TYPES_MGMT_FEE_VALUE', hasIndex: 5, valueIndex: 6},
    {hasKey: 'PRO_FORMA_REIMB_TYPES_HAS_INSURANCE', valueKey: 'PRO_FORMA_REIMB_TYPES_INSURANCE_VALUE', hasIndex: 7, valueIndex: 8},
    {hasKey: 'PRO_FORMA_REIMB_TYPES_HAS_TAXES', valueKey: 'PRO_FORMA_REIMB_TYPES_TAXES_VALUE', hasIndex: 9, valueIndex: 10},
];

function buildTenantTypeSaveRow({
    key,
    tenantTypes,
    originalTenantTypes,
    tenantTypesProForma,
    originalTenantTypesProForma,
    tenantTypeFields,
    propertyId,
    persistedRow,
}) {
    const tenantTypeObj = {};

    tenantTypeFields.forEach((prop) => {
        let value = null;
        let originalValue = null;
        if (prop.key.includes('PRO_FORMA')) {
            value = tenantTypesProForma[key].valueCells[prop.valueCellIndex].value;
            originalValue = originalTenantTypesProForma[key].valueCells[prop.valueCellIndex].value;
        } else {
            value = tenantTypes[key].valueCells[prop.valueCellIndex].value;
            originalValue = originalTenantTypes[key].valueCells[prop.valueCellIndex].value;
        }

        if (!tenantTypeFieldChanged(prop.type, value, originalValue)) {
            return;
        }

        switch (prop.type) {
            case 'string':
                tenantTypeObj[prop.key] = value;
                break;
            case 'number':
                tenantTypeObj[prop.key] = serializeTenantTypeNumber(value);
                break;
            case 'checkbox':
                tenantTypeObj[prop.key] = serializeTenantTypeCheckbox(value);
                // Always persist the paired PSF when a checkbox changes.
                TENANT_TYPE_CHECKBOX_VALUE_PAIRS.forEach((pair) => {
                    if (pair.hasKey !== prop.key) {
                        return;
                    }
                    const sourceRows = prop.key.includes('PRO_FORMA') ? tenantTypesProForma : tenantTypes;
                    const psf = sourceRows[key].valueCells[pair.valueIndex].value;
                    tenantTypeObj[pair.valueKey] = serializeTenantTypeNumber(
                        isCheckboxChecked(value) ? psf : 0,
                    );
                });
                break;
            default:
                break;
        }
    });

    if (!Object.keys(tenantTypeObj).length || !propertyId) {
        return null;
    }

    if (!tenantTypeObj.PROPERTY_ID) {
        tenantTypeObj.PROPERTY_ID = propertyId;
    }
    if (tenantTypeObj.REIMB_TYPES_INDEX == null) {
        tenantTypeObj.REIMB_TYPES_INDEX = parseInt(key, 10);
    }
    if (persistedRow && persistedRow.ARCIMEDES_ROW_ID != null && persistedRow.ARCIMEDES_ROW_ID !== '') {
        tenantTypeObj.ARCIMEDES_ROW_ID = persistedRow.ARCIMEDES_ROW_ID;
    }

    return tenantTypeObj;
}

export {
    getCheckboxValue,
    serializeTenantTypeNumber,
    serializeTenantTypeCheckbox,
    isCheckboxChecked,
    tenantTypeFieldChanged,
    indexTenantTypesByReimbIndex,
    resolveTenantTypeRow,
    shouldRecomputeReimbTypes,
    anyReimbTypeRecomputeNeeded,
    computeReimbCategoryPsf,
    roundReimbPsf,
    REIMB_PSF_DECIMALS,
    TENANT_TYPE_CHECKBOX_VALUE_PAIRS,
    buildTenantTypeSaveRow,
};
